import React from 'react';
import { format, isValid } from 'date-fns';

import P from '@hero/ui-kit/typography/P';

import { careReasonText } from '../constants';
import * as Style from '../style.module.scss';
import { Checklist, RunState } from '../types';
import CareTable from './CareTable';

const RUN_STATUSES = new Map([
    ['running', 'Running'],
    ['succeeded', 'Completed'],
    ['failed', 'Failed']
]);

const FAILURE_LABELS = new Map([
    ['month_locked', 'The month was locked before this run could start. The locked results stay.'],
    ['missing_active_import', 'A required import is missing for this month.'],
    ['import_period_not_month', 'An import covers a different month.'],
    ['candidate_codes_mismatch', 'The billing codes do not match the qualification rule.'],
    ['status_count_mismatch', 'The qualification results are inconsistent.'],
    ['unknown_rule_version', 'The qualification rule is unavailable.'],
    ['unsupported_partial_day_policy', 'The rule uses an unsupported eligibility policy.'],
    ['unexpected_error', 'Qualification failed unexpectedly. Try again.']
]);

const when = (value: string | null | undefined): string => {
    if (!value) return 'Not recorded';
    const date = new Date(value);
    return isValid(date) ? format(date, 'PPp') : 'Not recorded';
};

const importLabel = (type: string): string =>
    type === 'units'
        ? 'Billed units'
        : type === 'activity'
          ? 'Activity'
          : (careReasonText(type) ?? type);

const RunPanel: React.FC<{ run?: RunState; ruleVersion?: string; checklist?: Checklist }> = ({
    run,
    ruleVersion,
    checklist
}) => {
    const latest = run?.latest_run;
    const status = latest
        ? (RUN_STATUSES.get(latest.status) ?? careReasonText(latest.status))
        : 'Not run';
    // Legacy import details can be empty for a valid minutes-only month.
    const importTypes = run ? Object.keys(run.imports) : [];
    const importsValid = checklist?.steps.find((step) => step.step === 'imports_valid')?.done;
    const rows: React.ReactNode[][] | undefined = run
        ? [
              [
                  'Latest qualification',
                  <div key="qualification">
                      <strong>{status}</strong>
                      {latest ? (
                          <>
                              <P size="small" noDefaultMargin>
                                  {latest.status === 'running'
                                      ? `Started ${when(latest.started_at)}`
                                      : latest.finished_at
                                        ? `Finished ${when(latest.finished_at)}`
                                        : `Started ${when(latest.started_at)}`}
                              </P>
                              {latest.status === 'failed' ? (
                                  <P size="small" noDefaultMargin>
                                      {FAILURE_LABELS.get(latest.failure_code ?? '') ??
                                          (run.lock.locked
                                              ? 'Qualification failed.'
                                              : 'Qualification failed. Try again, or ask an administrator to review the failure.')}
                                  </P>
                              ) : null}
                          </>
                      ) : (
                          <P size="small" noDefaultMargin>
                              No qualification has been run for this month.
                          </P>
                      )}
                  </div>
              ],
              [
                  'Last successful qualification',
                  run.lock.locked
                      ? `The month is locked with attempt ${run.lock.locked_run_id ?? '—'}`
                      : run.succeeded_run_id === null
                        ? 'No successful qualification yet'
                        : run.succeeded_run_id === latest?.id
                          ? 'The latest attempt completed successfully'
                          : 'An earlier attempt completed successfully; select Calculate billable codes again before locking'
              ],
              [
                  latest ? 'Rule used by latest attempt' : 'Configured rule',
                  <div key="rule">
                      <strong>{latest?.rule_version ?? ruleVersion ?? 'Not available'}</strong>
                      {latest?.draft ? (
                          <P size="small" noDefaultMargin>
                              Draft rule — approval is required before its results can be used to
                              lock the month.
                          </P>
                      ) : null}
                      {latest && ruleVersion && latest.rule_version !== ruleVersion ? (
                          <P size="small" noDefaultMargin>
                              {`Currently configured rule: ${ruleVersion}`}
                          </P>
                      ) : null}
                  </div>
              ],
              ...(importTypes.length === 0
                  ? [
                        [
                            'Imports',
                            importsValid === true
                                ? 'Required imports are available for this month.'
                                : importsValid === false
                                  ? 'A required import is missing or covers another month. See Progress.'
                                  : 'Import status is not available yet.'
                        ]
                    ]
                  : []),
              ...importTypes.map((type): React.ReactNode[] => {
                  const item = run.imports[type];
                  return [
                      `${importLabel(type)} import`,
                      item ? (
                          <div key={type}>
                              <P size="small" noDefaultMargin>
                                  {`Received ${when(item.received_at)}`}
                              </P>
                              <P size="small" noDefaultMargin>
                                  {`${item.matched_source_rows} ${item.matched_source_rows === 1 ? 'row' : 'rows'} matched`}
                                  {item.unmatched_source_rows > 0
                                      ? ` · ${item.unmatched_source_rows} ${item.unmatched_source_rows === 1 ? 'row needs' : 'rows need'} a patient match`
                                      : ' · No unmatched rows'}
                              </P>
                              <P size="small" noDefaultMargin>
                                  {`Matching updated ${when(item.matched_at)}`}
                              </P>
                          </div>
                      ) : (
                          '—'
                      )
                  ];
              }),
              ...(run.lock.locked
                  ? [
                        [
                            'Lock details',
                            <div key="lock">
                                <P size="small" noDefaultMargin>
                                    {`Locked ${when(run.lock.locked_at)}`}
                                </P>
                                {run.lock.locked_by ? (
                                    <P
                                        size="small"
                                        noDefaultMargin
                                    >{`Locked by ${run.lock.locked_by}`}</P>
                                ) : null}
                                {run.lock.lock_reason ? (
                                    <P
                                        size="small"
                                        noDefaultMargin
                                    >{`Reason: ${run.lock.lock_reason}`}</P>
                                ) : null}
                            </div>
                        ]
                    ]
                  : [])
          ]
        : undefined;
    const technicalRows: React.ReactNode[][] = run
        ? [
              ['Latest attempt ID', latest?.id ?? '—'],
              ['Last successful attempt ID', run.succeeded_run_id ?? '—'],
              ['Population revision', run.population_revision],
              ['Operator revision', run.operator_revision],
              ...(latest?.failure_code ? [['Failure code', latest.failure_code]] : []),
              ...(run.lock.locked
                  ? [['Locked using attempt ID', run.lock.locked_run_id ?? '—']]
                  : []),
              ...Object.entries(run.imports).flatMap(([type, item]) => [
                  [`${importLabel(type)} import ID`, item.import_id],
                  [`${importLabel(type)} matching attempt ID`, item.attempt_id]
              ])
          ]
        : [];

    return (
        <CareTable
            collapsible
            title={`Run and lock${run ? ` — ${status} · ${run.lock.locked ? 'Locked' : 'Unlocked'}` : ''}`}
            columns={[]}
            gridTemplateColumns="minmax(0, 1fr) minmax(0, 3fr)"
            rows={rows}
            rowClassNames={rows?.map(() => Style.runRow)}
        >
            {run ? (
                <details>
                    <summary className={Style.technicalSummary}>
                        <strong>Technical details</strong>
                    </summary>
                    <CareTable
                        columns={['Detail', 'Value']}
                        gridTemplateColumns="minmax(0, 3fr) minmax(0, 2fr)"
                        rows={technicalRows}
                        rowClassNames={technicalRows.map(() => Style.runRow)}
                    />
                </details>
            ) : null}
        </CareTable>
    );
};

export default RunPanel;
